import { mapValues, omit } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { createInputStep } from 'dashboard/lib/workflows';
import {
  getAllModuleBlockIds,
  makeStepsConfigUIReady,
  stripViewSpecificAttributes,
} from 'dashboard/lib/workflows/steps-config-operations';
import { validateWorkflowConfiguration } from 'dashboard/lib/workflows/validation';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import {
  ALL_TRIGGER_TYPES,
  Trigger,
  TriggerAccount,
  TriggerApi,
  TriggerApiAttributes,
  TriggerCase,
  TriggerCaseAttributes,
  TriggerComputedField,
  TriggerComputedFieldAttributes,
  TriggerEvent,
  TriggerEventAttributes,
  TriggerRecurring,
  TriggerRecurringAttributes,
  TriggerSchedule,
  TriggerScheduleAttributes,
} from 'dashboard/models/trigger';
import { Workflow, WorkflowAttributes } from 'dashboard/models/workflow';
import {
  WorkflowDeployment,
  WorkflowDeploymentConfigurationLabel,
} from 'dashboard/models/workflow-deployment';
import {
  WorkflowStep,
  WorkflowVersion,
  WorkflowVersionAttributes,
  WorkflowVersionStatus,
} from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setInputStep,
  setPublishedWorkflowVersion,
  setStepContexts,
  setWorkflow,
  setWorkflowDeployment,
  setWorkflowVersion,
  setWorkflowVersionErrors,
} from 'dashboard/redux/actions/workflow';
import { addWorkflow } from 'dashboard/redux/actions/workflows';
import { workflowBlockEndpoints } from 'dashboard/redux/api/workflow-block';
import { workflowSchemaEndpoints } from 'dashboard/redux/api/workflow-schema';
import {
  getIsPlatformObjectJsonSchemaEnabled,
  getPlatformObjectJsonSchema,
  getReferencedBlocksById,
  getValidationContext,
} from 'dashboard/redux/selectors/workflow-editor-context';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiOptions } from 'lib/clients/ApiClient';
import ApiResponseError from 'lib/errors/ApiResponseError';
import {
  ApiEndpoint,
  ApiModel,
  ApiPayload,
  ApiResource,
  ApiSingletonPayload,
} from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export type TriggerApiParams = { type: ApiModel.TriggerApi; extra: TriggerApiAttributes };

export type TriggerEventParams = {
  type: ApiModel.TriggerEvent;
  extra: TriggerEventAttributes;
};

export type TriggerCaseParams = {
  type: ApiModel.TriggerCase;
  extra: TriggerCaseAttributes;
};

export type TriggerScheduleParams = {
  type: ApiModel.TriggerSchedule;
  extra: TriggerScheduleAttributes;
};

export type TriggerRecurringParams = {
  type: ApiModel.TriggerRecurring;
  extra: TriggerRecurringAttributes;
};

export type TriggerComputedFieldParams = {
  type: ApiModel.TriggerComputedField;
  extra: TriggerComputedFieldAttributes;
};

export type TriggerParams =
  | TriggerScheduleParams
  | TriggerRecurringParams
  | TriggerComputedFieldParams
  | TriggerApiParams
  | TriggerEventParams
  | TriggerCaseParams;

export const createWorkflow = (
  environmentId: string,
  name: string,
  trigger: TriggerParams,
  description: string,
  builderName?: string,
): DashboardThunkAction<Promise<{ redirectPath: string }>> => {
  return async () => {
    const { data: newWorkflow, included = [] } = await SessionClientDashboard.postResourceCreate<
      Workflow,
      ApiSingletonPayload<Partial<WorkflowAttributes>>
    >(
      {
        type: ApiModel.Workflow,
        attributes: {
          version: 2,
          name,
          description,
          'environment-id': environmentId,
        },
      },
      {
        meta: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          builder: builderName,
          triggerType: trigger.type,
        },
      },
    );

    const workflowVersion = included.find(
      (include: ApiResource) => include.type === ApiModel.WorkflowVersion,
    ) as WorkflowVersion;

    if (builderName == null) {
      let response;
      if (trigger.type === ApiModel.TriggerApi) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerApi,
          ApiSingletonPayload<Partial<TriggerApiAttributes & { 'environment-id': string }>>
        >({
          type: ApiModel.TriggerApi,
          attributes: {
            'environment-id': environmentId,
          },
        });
      } else if (trigger.type === ApiModel.TriggerSchedule) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerSchedule,
          ApiSingletonPayload<Partial<TriggerScheduleAttributes & { 'environment-id': string }>>
        >({
          type: ApiModel.TriggerSchedule,
          attributes: {
            'environment-id': environmentId,
            'trigger-object-type': trigger.extra['trigger-object-type'],
          },
        });
      } else if (trigger.type === ApiModel.TriggerRecurring) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerSchedule,
          ApiSingletonPayload<Partial<TriggerScheduleAttributes & { 'environment-id': string }>>
        >({
          type: ApiModel.TriggerRecurring,
          attributes: {
            'environment-id': environmentId,
            'trigger-object-type': trigger.extra['trigger-object-type'],
          },
        });
      } else if (trigger.type === ApiModel.TriggerComputedField) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerComputedField,
          ApiSingletonPayload<
            Partial<TriggerComputedFieldAttributes & { 'environment-id': string }>
          >
        >({
          type: ApiModel.TriggerComputedField,
          attributes: {
            'environment-id': environmentId,
            'field-key': trigger.extra['field-key'],
            config: trigger.extra['config'],
            'field-schema-object-token': trigger.extra['field-schema-object-token'],
          },
        });
      } else if (trigger.type === ApiModel.TriggerEvent) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerEvent,
          ApiSingletonPayload<TriggerEventAttributes & { 'environment-id': string }>
        >({
          type: ApiModel.TriggerEvent,
          attributes: {
            'environment-id': environmentId,
            'event-name': trigger.extra['event-name'],
            rules: true,
            'template-filters': trigger.extra['template-filters'],
          },
        });
      } else if (trigger.type === ApiModel.TriggerCase) {
        response = await SessionClientDashboard.postResourceCreate<
          TriggerCase,
          ApiSingletonPayload<Partial<TriggerCaseAttributes> & { 'environment-id': string }>
        >({
          type: ApiModel.TriggerCase,
          attributes: {
            'environment-id': environmentId,
          },
        });
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { data: newTrigger } = response;

      await SessionClientDashboard.updateResource<
        WorkflowVersion,
        ApiPayload<WorkflowVersionAttributes & { 'trigger-id': string; 'trigger-type': ApiModel }>
      >({
        ...workflowVersion,
        attributes: {
          ...workflowVersion.attributes,
          'trigger-id': newTrigger.id,
          'trigger-type': newTrigger.type,
          'steps-config': [],
        },
      });
    }

    let redirectPath;
    if (trigger.type === ApiModel.TriggerCase) {
      redirectPath = `/dashboard/case-actions/${newWorkflow.id}/${workflowVersion.id}`;
    } else {
      redirectPath = `/dashboard/workflows/${newWorkflow.id}/${workflowVersion.id}`;
    }

    return { redirectPath };
  };
};

/**
 * @name archiveWorkflow
 *
 * Takes a workflow and archives it.
 * The workflow is not accessible on dashboard after archival.
 * Pushes you back to workflows index page
 */
export const archiveWorkflow = (workflow: Workflow): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { data: deletedWorkflow } = await SessionClientDashboard.deleteResource<Workflow>(
      ApiEndpoint.Workflow,
      workflow.id,
    );

    dispatch(setWorkflow(deletedWorkflow));
    dispatch(addWorkflow(deletedWorkflow));
    dispatch(showMessage('Successfully archived workflow.', MessageType.Success));
  };
};

/**
 * @param workflowId id of workflow to load
 * @param workflowVersionId id of of workflow version to load
 * N.B. Only use for 2.0
 **/
export const loadWorkflow = (
  workflowId: string,
): DashboardThunkAction<
  Promise<{ workflow: Workflow; workflowDeployment: WorkflowDeployment | null }>
> => {
  return async (dispatch) => {
    const { data: workflow, included } = await SessionClientDashboard.getResource<Workflow>(
      ApiEndpoint.Workflow,
      workflowId,
    );

    const workflowDeployment =
      (included || []).find(
        (include: ApiResource): include is WorkflowDeployment =>
          include.type === ApiModel.WorkflowDeployment,
      ) || null;

    dispatch(setWorkflow(workflow));
    dispatch(setWorkflowDeployment(workflowDeployment));
    return { workflow, workflowDeployment };
  };
};

export const loadWorkflowVersion = (
  workflowVersionId: string,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const { data: workflowVersion, included: includedVersion = [] } =
      await SessionClientDashboard.getResource<WorkflowVersion>(
        ApiEndpoint.WorkflowVersion,
        workflowVersionId,
      );

    const trigger = includedVersion.find((include: ApiResource): include is Trigger =>
      ALL_TRIGGER_TYPES.some((triggerType) => triggerType === include.type),
    );
    const stepsConfig = await dispatch(setWorkflowEditorContext(workflowVersion, trigger));

    if (workflowVersion.attributes.status === WorkflowVersionStatus.Draft) {
      // We implicitly allows editing a published version, then copy everything into a new version
      // right before saving it to server. This mostly works, except the description should not
      // not be copied over. However, if we are editing a version that is already a draft, then we need
      // to preserve the description.
      dispatch(
        setWorkflowVersion({
          ...workflowVersion,
          attributes: {
            ...workflowVersion.attributes,
            'steps-config': stepsConfig,
          },
        }),
      );
    } else {
      dispatch(
        setWorkflowVersion({
          ...workflowVersion,
          attributes: {
            // The current editable version should not copy over the published version's description
            ...omit(['description'], workflowVersion.attributes),
            'steps-config': stepsConfig,
          },
        }),
      );
    }
  };
};

export const setWorkflowEditorContext = (
  workflowVersion: WorkflowVersion,
  trigger?: Trigger,
  includeBlockInnerContext?: boolean,
): DashboardThunkAction<Promise<WorkflowStep[]>> => {
  return async (dispatch, getState) => {
    const moduleBlockIds = getAllModuleBlockIds(workflowVersion.attributes['steps-config']);
    const promises = moduleBlockIds.map((id) => {
      const action = workflowBlockEndpoints.getWorkflowBlock.initiate(id);
      return dispatch(action).unwrap();
    });
    const moduleBlockResponses = await Promise.all(promises);
    const blocksByBlockId = moduleBlockResponses.reduce((hash, blockResponse) => {
      return {
        ...hash,
        [blockResponse.data.id]: blockResponse.data,
      };
    }, {});
    const blockTemplateFilters = moduleBlockResponses.flatMap(
      (blockResponse) => blockResponse.data.attributes['template-filters'],
    );
    const platformObjectJsonSchema = getIsPlatformObjectJsonSchemaEnabled(getState())
      ? await dispatch(
          workflowSchemaEndpoints.getBaseWorkflowObjectJsonSchemas.initiate([
            ...blockTemplateFilters,
            ...workflowVersion.attributes['template-filters'],
          ]),
        ).unwrap()
      : undefined;

    const rootState = getState();
    const workflowSchema = rootState.workflowSchema ?? {};
    const showErrorObjects =
      rootState.featureFlags.featureFlags['global:workflow-error-variables'] ?? false;
    const inputStep = trigger ? createInputStep({ trigger }) : undefined;
    const { stepsConfig, stepContexts } = makeStepsConfigUIReady({
      steps: workflowVersion.attributes['steps-config'],
      inputStep,
      workflowSchema,
      blocksByBlockId,
      includeBlockInnerContext,
      platformObjectJsonSchema,
      showErrorObjects,
    });
    inputStep && dispatch(setInputStep(inputStep));
    dispatch(setStepContexts(stepContexts));
    return stepsConfig;
  };
};

export const loadPublishedWorkflowVersion = (
  workflow: Workflow,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const publishedWorkflowVersionId = workflow.relationships['latest-published-version']?.data?.id;
    if (publishedWorkflowVersionId) {
      const { data: workflowVersion } = await SessionClientDashboard.getResource<WorkflowVersion>(
        ApiEndpoint.WorkflowVersion,
        publishedWorkflowVersionId,
      );

      dispatch(setPublishedWorkflowVersion(workflowVersion));
    }
  };
};
/**
 * @name saveWorkflowVersion
 * @param workflowId ID of Workflow
 * @param workflowVersion Workflow Version to save
 *
 * If editing a published version:
 * 1. Create a new workflow version (will inherit from published version)
 * 2. Update the newly created version with updated values
 *
 * If editing a newly created version (draft):
 * 1. Update with form values
 */
export const saveWorkflowVersion = (
  workflowId: string,
  workflowVersion: WorkflowVersion,
  trigger: Trigger,
  meta?: ApiOptions['meta'],
): DashboardThunkAction<Promise<string | undefined>> => {
  return async (dispatch, getState) => {
    try {
      const [workflow, workflowVersionUpdated, triggerUpdated] = await saveVersion(
        workflowId,
        workflowVersion,
        trigger,
        { meta },
      );
      const inputStep = createInputStep({ trigger: triggerUpdated });
      const platformObjectJsonSchema = getPlatformObjectJsonSchema(getState());
      workflowVersionUpdated.attributes['steps-config'] = makeStepsConfigUIReady({
        steps: workflowVersionUpdated.attributes['steps-config'],
        inputStep,
        workflowSchema: getState().workflowSchema,
        platformObjectJsonSchema,
      }).stepsConfig;

      dispatch(setInputStep(inputStep));
      dispatch(setWorkflowVersion(workflowVersionUpdated));
      dispatch(setWorkflow(workflow));
      return workflowVersionUpdated.id;
    } catch (err) {
      if (err instanceof ApiResponseError) {
        const title = err?.errors?.[0].title;
        const message = err?.errors?.[0].details;
        if (title && message) {
          console.error(`Error saving workflow. ${title}: ${message}`);
        } else {
          console.error('Error saving workflow', JSON.stringify(err, null, 2));
        }
      }

      dispatch(
        showMessage('Failed to save workflow. Please try again later.', MessageType.Failure),
      );
    }
  };
};

export const updateWorkflow = (
  workflow: Workflow,
  attributes: { name?: string; description?: string },
): DashboardThunkAction => {
  return async (dispatch) => {
    const { id: workflowId } = workflow;
    const { data: updatedWorkflow } = await SessionClientDashboard.updateResource<Workflow>({
      type: ApiModel.Workflow,
      id: workflowId,
      attributes,
    });

    dispatch(setWorkflow(updatedWorkflow));
    dispatch(showMessage('Successfully updated workflow.', MessageType.Success));
  };
};

export const cloneWorkflow =
  (
    workflow: Workflow,
    name: string | null,
  ): DashboardThunkAction<Promise<{ redirectPath: string }>> =>
  async () => {
    const { data: clonedWorkflow, included = [] } = await SessionClientDashboard.postResourceAction<
      Workflow,
      {
        type: ApiModel.Workflow;
        attributes: { name: string | null };
        id: string;
      }
    >(
      {
        type: ApiModel.Workflow,
        attributes: {
          name: name,
        },
        id: workflow.id,
      },
      'clone',
    );

    const latestVersionOfClonedWorkflow = included.find(
      (include: ApiResource): include is WorkflowVersion =>
        include.type === ApiModel.WorkflowVersion,
    );

    return {
      redirectPath: `/dashboard/workflows/${clonedWorkflow.id}/${latestVersionOfClonedWorkflow?.id}`,
    };
  };

export const restoreWorkflowDraft = (
  workflowId: string,
  workflowVersionId: string,
): DashboardThunkAction<Promise<string | undefined>> => {
  return async (dispatch) => {
    const { data: workflowUpdated } = await SessionClientDashboard.postResourceAction<Workflow>(
      { id: workflowId, type: ApiModel.Workflow },
      'restore-draft',
      {
        meta: { 'workflow-version-id': workflowVersionId },
      },
    );

    dispatch(setWorkflow(workflowUpdated));
    return workflowUpdated.relationships['latest-draft-version'].data.id;
  };
};

export const configureWorkflowEnvironments = (
  workflow: Workflow,
  activeEnvironmentIds: string[],
  workflowVersion: WorkflowVersion,
  rolloutPercentage: number,
  previousWorkflowVersionId?: string,
): DashboardThunkAction => {
  return async (dispatch) => {
    try {
      const versionConfigurations = [
        {
          'workflow-version-id': workflowVersion.id,
          percentage: rolloutPercentage,
          label: WorkflowDeploymentConfigurationLabel.Treatment,
        },
        ...(previousWorkflowVersionId && rolloutPercentage < 100
          ? [
              {
                'workflow-version-id': previousWorkflowVersionId,
                percentage: 100 - rolloutPercentage,
                label: WorkflowDeploymentConfigurationLabel.Control,
              },
            ]
          : []),
      ];
      const meta = {
        'active-environment-ids': activeEnvironmentIds,
        ...(versionConfigurations && { 'version-configurations': versionConfigurations }),
      };
      const { data: refreshedWorkflow, included } =
        await SessionClientDashboard.postResourceAction<Workflow>(
          { id: workflow.id, type: workflow.type },
          'configure-environments',
          {
            meta,
          },
        );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const workflowDeployment = included.find(
        (include: ApiResource) => include.type === ApiModel.WorkflowDeployment,
      ) as WorkflowDeployment;

      dispatch(setWorkflow(refreshedWorkflow));
      dispatch(setWorkflowDeployment(workflowDeployment));
      dispatch(showMessage('Successfully configured workflow.', MessageType.Success));
    } catch (e) {
      dispatch(
        showMessage('Failed to configure workflow. Please try again later.', MessageType.Failure),
      );
    }
  };
};

export const rolloutWorkflowVersion = (
  workflow: Workflow,
  workflowVersion: WorkflowVersion,
  trigger: Trigger,
  rolloutPercentage: number,
  isDirty: boolean,
  environmentIds: string[],
  options: {
    description?: string;
    meta?: ApiOptions['meta'];
  } = {},
): DashboardThunkAction<Promise<string | undefined>> => {
  return async (dispatch, getState) => {
    try {
      const previousPublishedVersionId =
        workflow.relationships['latest-published-version']?.data?.id;
      let nextPublishedVersion = workflowVersion;
      let updatedTrigger = trigger;
      // TODO: remove this if statement since users can no longer roll out a draft version
      if (isDirty || workflowVersion.attributes.status === WorkflowVersionStatus.Draft) {
        const [_, newPublishedVersion, newTrigger] = await publishVersion(
          workflow.id,
          workflowVersion,
          trigger,
          isDirty,
          options,
        );
        nextPublishedVersion = newPublishedVersion;
        updatedTrigger = newTrigger;
      }

      const meta = {
        'previous-workflow-version-id': previousPublishedVersionId,
        'next-workflow-version-id': nextPublishedVersion.id,
        'rollout-percentage': rolloutPercentage,
        'environment-ids': environmentIds,
      };
      const { data: workflowAfterRollout, included } =
        await SessionClientDashboard.postResourceAction<Workflow>(
          { id: workflow.id, type: workflow.type },
          'rollout',
          {
            meta,
          },
        );
      const inputStep = createInputStep({ trigger: updatedTrigger });
      const platformObjectJsonSchema = getPlatformObjectJsonSchema(getState());
      nextPublishedVersion.attributes['steps-config'] = makeStepsConfigUIReady({
        steps: nextPublishedVersion.attributes['steps-config'],
        inputStep,
        workflowSchema: getState().workflowSchema,
        platformObjectJsonSchema,
      }).stepsConfig;

      dispatch(
        setWorkflowVersion({
          ...nextPublishedVersion,
          attributes: {
            // The current editable version should not copy over the newly published version's description
            ...omit(['description'], nextPublishedVersion.attributes),
          },
        }),
      );
      dispatch(setWorkflow(workflowAfterRollout));
      dispatch(setInputStep(inputStep));
      if (included) {
        const workflowDeployment = included.find(
          (include: ApiResource): include is WorkflowDeployment =>
            include.type === ApiModel.WorkflowDeployment,
        );
        if (workflowDeployment) {
          dispatch(setWorkflowDeployment(workflowDeployment));
        }
      }
      return nextPublishedVersion.id;
    } catch (err) {
      // (evan.qi) save the description in redux state when rollout fails
      dispatch(
        setWorkflowVersion({
          ...workflowVersion,
          attributes: { ...workflowVersion.attributes, description: options.description },
        }),
      );

      if (!(err instanceof ApiResponseError)) {
        dispatch(
          showMessage('Failed to rollout workflow. Please try again later.', MessageType.Failure),
        );
        return;
      }

      const errors = err?.errors ?? [];

      for (const error of errors) {
        const message = error.details;

        if (isPermissionsError(err)) {
          dispatch(showMessage(permissionsErrorMessage(err), MessageType.Failure));
        } else if (typeof message === 'string') {
          dispatch(showMessage(message, MessageType.Failure));
        } else {
          dispatch(
            showMessage('Failed to rollout workflow. Please try again later.', MessageType.Failure),
          );
        }
      }
    }
  };
};

export const rollbackWorkflow = (
  workflow: Workflow,
  environmentIds: string[],
): DashboardThunkAction<Promise<{ redirectPath: string } | void>> => {
  return async (dispatch) => {
    try {
      const meta = {
        'environment-ids': environmentIds,
      };
      const { data: workflowUpdated, included } =
        await SessionClientDashboard.postResourceAction<Workflow>(
          { id: workflow.id, type: workflow.type },
          'rollback',
          { meta },
        );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const workflowVersionUpdated = included.find(
        (include: ApiResource) =>
          include.id ===
          (workflowUpdated.relationships['latest-draft-version']?.data?.id ??
            workflowUpdated.relationships['latest-published-version']?.data?.id),
      ) as WorkflowVersion;

      dispatch(setWorkflowVersion(workflowVersionUpdated));
      dispatch(setWorkflow(workflowUpdated));
      dispatch(setWorkflowDeployment(null));
      dispatch(showMessage('Successfully rolled back workflow.', MessageType.Success));

      const toLink = `/dashboard/workflows/${workflow.id}/${workflowVersionUpdated.id}`;
      return {
        redirectPath: toLink,
      };
    } catch (e) {
      dispatch(
        showMessage('Failed to roll back workflow. Please try again later.', MessageType.Failure),
      );
    }
  };
};

// If published, dup then save. Else just save.
async function saveVersion(
  workflowId: string,
  workflowVersion: WorkflowVersion,
  trigger: Trigger,
  options: {
    description?: string;
    meta?: ApiOptions['meta'];
  } = {},
): Promise<[Workflow, WorkflowVersion, Trigger]> {
  const { meta, description } = options;
  const metaArg = meta ? { meta } : undefined;
  const workflowVersionId =
    workflowVersion.attributes.status === WorkflowVersionStatus.Published
      ? (
          await SessionClientDashboard.postResourceCreate<
            WorkflowVersion,
            {
              type: ApiModel.WorkflowVersion;
              attributes: { 'workflow-id': string; 'src-workflow-version-id': string };
            }
          >(
            {
              type: ApiModel.WorkflowVersion,
              attributes: {
                'workflow-id': workflowId,
                'src-workflow-version-id': workflowVersion.id,
              },
            },
            metaArg,
          )
        ).data.id
      : workflowVersion.id;

  const { data: workflowVersionUpdated, included = [] } =
    await SessionClientDashboard.updateResource<WorkflowVersion>({
      type: ApiModel.WorkflowVersion,
      id: workflowVersionId,
      attributes: {
        // these are the only three attributes that are updatable
        // FIXME: Deep underscore nested KVP
        'steps-config': stripViewSpecificAttributes(workflowVersion.attributes['steps-config']),
        outputs: workflowVersion.attributes.outputs,
        description: description || workflowVersion.attributes.description,
        'time-out-minutes': workflowVersion.attributes['time-out-minutes'],
      },
    });

  const newTrigger =
    included.find((include): include is Trigger =>
      ALL_TRIGGER_TYPES.some((triggerType) => triggerType === include.type),
    ) || trigger;

  const triggerUpdated = await (async () => {
    if (trigger.type === ApiModel.TriggerEvent) {
      return (
        await SessionClientDashboard.updateResource<TriggerEvent>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerEvent,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerAccount) {
      return (
        await SessionClientDashboard.updateResource<TriggerAccount>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerAccount,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerApi) {
      return (
        await SessionClientDashboard.updateResource<TriggerApi>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerApi,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerSchedule) {
      return (
        await SessionClientDashboard.updateResource<TriggerSchedule>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerSchedule,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerRecurring) {
      return (
        await SessionClientDashboard.updateResource<TriggerRecurring>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerRecurring,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerComputedField) {
      return (
        await SessionClientDashboard.updateResource<TriggerComputedField>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerComputedField,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerCase) {
      return (
        await SessionClientDashboard.updateResource<TriggerCase>({
          id: newTrigger.id,
          attributes: trigger.attributes,
          type: ApiModel.TriggerCase,
        })
      ).data;
    }

    if (trigger.type === ApiModel.TriggerDirect) {
      return trigger;
    }
  })();

  const workflow = included.find(
    (include: ApiResource): include is Workflow => include.type === ApiModel.Workflow,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return [workflow, workflowVersionUpdated, triggerUpdated];
}

async function publishVersion(
  workflowId: string,
  workflowVersion: WorkflowVersion,
  trigger: Trigger,
  isDirty: boolean,
  options: {
    description?: string;
    meta?: ApiOptions['meta'];
  } = {},
): Promise<[Workflow, WorkflowVersion, Trigger]> {
  const { description } = options;
  let workflowVersionToPublish = workflowVersion;
  let newTrigger = trigger;
  if (isDirty || description != null) {
    const [_, workflowVersionUpdated, updatedTrigger] = await saveVersion(
      workflowId,
      workflowVersion,
      trigger,
      options,
    );
    workflowVersionToPublish = workflowVersionUpdated;
    newTrigger = updatedTrigger;
  }

  const { data: workflowVersionPublished, included } =
    await SessionClientDashboard.postResourceAction<WorkflowVersion>(
      {
        id: workflowVersionToPublish.id,
        type: workflowVersionToPublish.type,
      },
      'publish',
    );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const workflowUpdated = included.find(
    (include: ApiResource) => include.type === ApiModel.Workflow,
  ) as Workflow;

  return [workflowUpdated, workflowVersionPublished, newTrigger];
}

export const runWorkflow = (workflow: Workflow): DashboardThunkAction => {
  return async (dispatch) => {
    try {
      const response = await SessionClientDashboard.postResourceAction<Workflow>(
        { id: workflow.id, type: workflow.type },
        'run',
      );
      dispatch(showMessage('Your workflow is now active', MessageType.Success));
      dispatch(setWorkflow(response.data));
    } catch (e) {
      dispatch(
        showMessage('Failed to activate workflow. Please try again later.', MessageType.Failure),
      );
    }
  };
};

export const pauseWorkflow = (workflow: Workflow): DashboardThunkAction => {
  return async (dispatch) => {
    try {
      const response = await SessionClientDashboard.postResourceAction<Workflow>(
        { id: workflow.id, type: workflow.type },
        'pause',
      );
      dispatch(showMessage('Your workflow is now inactive', MessageType.Success));
      dispatch(setWorkflow(response.data));
    } catch (e) {
      dispatch(
        showMessage('Failed to deactivate workflow. Please try again later.', MessageType.Failure),
      );
    }
  };
};

export const validateWorkflow = (
  externalIntegrationVendors: ExternalIntegrationVendor[],
): DashboardThunkAction<void> => {
  return (dispatch, getState) => {
    const rootState = getState();
    const { workflowVersion } = rootState.workflow.present;
    const blocksById = getReferencedBlocksById(rootState);

    const errors = validateWorkflowConfiguration({
      validationContext: getValidationContext(rootState),
      outputs: workflowVersion.attributes.outputs,
      externalIntegrationVendors,
      timeOutMinutes: workflowVersion.attributes['time-out-minutes'],
      moduleBlockInputsByBlockId: mapValues((block) => block.attributes.inputs, blocksById),
    });
    dispatch(setWorkflowVersionErrors(errors));
  };
};

export const discardDraftWorkflowVersion = (
  workflow: Workflow,
  workflowVersionId: string,
): DashboardThunkAction<Promise<void | { redirectPath: string }>> => {
  return async (dispatch) => {
    const latestPublishedVersionId = workflow.relationships['latest-published-version'].data.id;
    const { data: workflowVersion } = await SessionClientDashboard.deleteResource<WorkflowVersion>(
      ApiEndpoint.WorkflowVersion,
      workflowVersionId,
    );

    if (workflowVersion.attributes.status === WorkflowVersionStatus.Archived) {
      await dispatch(loadWorkflowVersion(latestPublishedVersionId));
      await dispatch(loadWorkflow(workflow.id));
      dispatch(showMessage('Successfully discarded draft version.', MessageType.Success));
      return { redirectPath: `/dashboard/workflows/${workflow.id}/${latestPublishedVersionId}` };
    } else {
      dispatch(showMessage('Failed to discard draft version.', MessageType.Failure));
    }
  };
};
